import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

class CrossSellComponent extends HTMLElement {
  constructor() {
    super();

    this.selects = null;
    this.form = null;
    this.checkbox = null;
    this.button = null;
    this.buttonLabel = null;
    this.isAdded = false;
    this.isInCart = false;
    this.isInASlider = false;
    this.productHandle = null;
  }

  connectedCallback() {
    this.selects = this.querySelectorAll('[data-ref="variant-select"]');
    this.form = this.querySelector('[data-ref="add-to-cart-form"]');
    this.checkbox = this.querySelector('[data-ref="add-to-cart-checkbox"]');
    this.button = this.querySelector('[data-ref="add-to-cart-button"]');
    this.buttonLabel = this.querySelector('[data-ref="add-to-cart-button-label"]');
    this.isInCart = this.closest('cart-drawer') || this.closest('cart-component') ? true : false;
    this.isInASlider = this.closest('slider-component') ? true : false;
    this.productHandle = this.dataset.productHandle;

    this.checkbox?.addEventListener('change', this.#handleCheckboxChange);
    this.button?.addEventListener('click', this.#handleButtonClick);

    this.selects.forEach((select) => {
      select.addEventListener('change', this.#handleSelectChange);
    });

    if (this.isInCart) this.#synchronizeWithCartContent();
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener('change', this.#handleCheckboxChange);
    this.button?.removeEventListener('click', this.#handleButtonClick);

    this.selects.forEach((select) => {
      select.removeEventListener('change', this.#handleSelectChange);
    });
  }

  // Coche = ajout immediat au panier. Decoche = retrait immediat. Demande de
  // Pauline le 17/09/2026 : le geste doit etre aussi simple pour la cliente
  // que cocher une case, dans les deux sens.
  #handleCheckboxChange = async () => {
    if (this.checkbox.checked) {
      await this.#addToCart();
    } else {
      await this.#removeFromCart();
    }
  };

  // Meme geste que la case a cocher (clic = ajoute, reclic = retire), mais en
  // bouton avec un libelle qui passe de "Ajouter" a "Ajoute" pour confirmer
  // visuellement l'ajout. Demande de Pauline le 21/09/2026, pour les deux
  // brosses du panier uniquement.
  #handleButtonClick = async () => {
    if (this.isAdded) {
      await this.#removeFromCart();
    } else {
      await this.#addToCart();
    }
  };

  #setButtonAdded(added) {
    if (!this.button) return;
    this.isAdded = added;
    if (this.buttonLabel) {
      this.buttonLabel.textContent = added ? this.dataset.addedLabel : this.button.dataset.addLabel;
    }
    this.button.classList.toggle('is-added', added);
  }

  #cartSections() {
    const sections = ['auto-gifts'];
    if (document.querySelector('cart-drawer')) {
      const section_cart_drawer_id = document.querySelector('cart-drawer').dataset.sectionId;
      sections.push(section_cart_drawer_id);
    }
    return sections;
  }

  #setLoading(isLoading) {
    this.form.classList.toggle('is-loading', isLoading);
    if (this.checkbox) this.checkbox.disabled = isLoading;
    if (this.button) this.button.disabled = isLoading;
  }

  #addToCart = async () => {
    this.#setLoading(true);

    const items = [];
    this.selects.forEach((select) => {
      items.push({ id: select.value, quantity: 1 });
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: this.#cartSections().join(',') }),
      });

      const data = await response.json();

      if (data.status) {
        // Shopify renvoie un statut d'erreur (rupture de stock, etc.) : on decoche
        if (this.checkbox) this.checkbox.checked = false;
        this.#setButtonAdded(false);
        console.error('Cross-sell add error:', data.message || data.description);
      } else {
        this.#setButtonAdded(true);
        this.updateCart(data, items.length);
      }
    } catch (error) {
      if (this.checkbox) this.checkbox.checked = false;
      this.#setButtonAdded(false);
      console.error('Error:', error);
    } finally {
      this.#setLoading(false);
    }
  };

  #removeFromCart = async () => {
    this.#setLoading(true);

    const variantIds = Array.from(this.selects).map((select) => select.value);
    let lastData = null;

    try {
      for (const id of variantIds) {
        const response = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, quantity: 0, sections: this.#cartSections().join(',') }),
        });

        lastData = await response.json();
      }

      if (lastData) {
        this.#setButtonAdded(false);
        this.updateCart(lastData, -variantIds.length);
      }
    } catch (error) {
      if (this.checkbox) this.checkbox.checked = true;
      this.#setButtonAdded(true);
      console.error('Error:', error);
    } finally {
      this.#setLoading(false);
    }
  };

  #handleSelectChange = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.updateBundlePrices();

    if (this.dataset.dynamicVariantImage === 'true') {
      this.updateImage();
    }
  };

  updateCart(data, itemDelta) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    } else {
      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();

      let newItemCount;
      if (currentItemCount != null) {
        newItemCount = currentItemCount + itemDelta;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'cross-sell',
            sections: data.sections,
          },
        ),
      );
    }
  }

  updateBundlePrices() {
    const priceContainer = this.querySelector('.price-container .price');
    const compareAtPriceContainer = this.querySelector('.price-container .compare-at-price');

    let price = 0;
    let compareAtPrice = 0;

    this.selects.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];

      if (selectedOption != null) {
        price += parseInt(selectedOption.dataset.price);
        compareAtPrice += parseInt(selectedOption.dataset.compareAtPrice) || parseInt(selectedOption.dataset.price);
      } else {
        price += parseInt(select.dataset.price);
        compareAtPrice += parseInt(select.dataset.compareAtPrice) || parseInt(select.dataset.price);
      }
    });

    priceContainer.innerText = formatMoney(price);

    if (compareAtPrice > price) {
      compareAtPriceContainer.innerText = formatMoney(compareAtPrice);
    } else {
      compareAtPriceContainer.innerText = '';
    }
  }

  updateImage() {
    const image = this.querySelector('[data-ref="cross-sell-image"]');
    if (!image) return;

    const selectedOption = this.selects[0].options[this.selects[0].selectedIndex];
    if (!selectedOption.dataset.image) return;

    image.src = selectedOption.dataset.image;
  }

  // Si le cross-sell est dans un panier, et que le produit est dans le panier, on masque le cross-sell
  #synchronizeWithCartContent() {
    let cartItems = null;
    if (this.closest('cart-drawer')) cartItems = this.closest('cart-drawer').querySelectorAll('[data-ref="cart-item"]');
    if (this.closest('cart-component')) cartItems = this.closest('cart-component').querySelectorAll('[data-ref="cart-item"]');

    if (!cartItems) return;

    const closestSlider = this.closest('slider-component');

    cartItems.forEach((cartItem) => {
      if (cartItem.dataset.productHandle === this.productHandle) {
        if (this.button) {
          this.#setButtonAdded(true);
        } else if (this.isInASlider) {
          closestSlider.querySelectorAll('.splide__slide').forEach((slide, index) => {
            if (slide == this.closest('.splide__slide')) {
              closestSlider.remove(index);
            }
          });
        } else {
          this.classList.add('hidden');
        }
      }
    });
  }
}

if (!customElements.get('cross-sell-component')) {
  customElements.define('cross-sell-component', CrossSellComponent);
}
